import { ChevronLeftIcon } from '@heroicons/react/24/outline';
import * as Sentry from '@sentry/react';
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { usePatientPortal } from '../../contexts/PatientPortalContext';
import { useInvalidateAppointmentSurveysOnUnmount } from '../../hooks/useAppointmentSurveys';
import surveyService, { RequestDetail } from '../../services/survey.service';
import { Skeleton } from '../common/Skeleton';
import StepBasicInformation from '../request/StepBasicInformation';
import StepInsurance from '../request/StepInsurance';

export type RequestFormKind = 'basicInformation' | 'insurance';

const verbiage = {
  en: {
    back: 'Back',
    notFoundTitle: 'Not found',
    notFoundDetail: "You don't have an active request for this step.",
  },
  es: {
    back: 'Volver',
    notFoundTitle: 'No encontrado',
    notFoundDetail: 'No tienes una solicitud activa para este paso.',
  },
};

type RunnerStatus = 'loading' | 'ready' | 'notfound' | 'error';

const RequestFormRunner: React.FC<{ kind: RequestFormKind; basePath: string }> = ({
  kind,
  basePath,
}) => {
  const { requestId } = useParams<{ requestId: string }>();
  const navigate = useNavigate();
  const { selectedPatient } = usePatientPortal();
  const primaryColor = selectedPatient?.clinic?.primaryColor || '#77bd43';
  const clinicPath = (selectedPatient?.clinic as { path?: string } | undefined)?.path;

  // Refresh Home's pre-visit task list when leaving — otherwise the
  // 30s staleTime keeps the cached entry showing as not-complete.
  useInvalidateAppointmentSurveysOnUnmount();

  const [status, setStatus] = useState<RunnerStatus>('loading');
  const [detail, setDetail] = useState<RequestDetail | null>(null);

  const language: 'en' | 'es' = (detail?.language === 'es' ? 'es' : 'en') as 'en' | 'es';
  const v = useMemo(() => verbiage[language], [language]);

  useEffect(() => {
    let cancelled = false;

    if (!clinicPath || !requestId) {
      setStatus('notfound');

      return;
    }

    setStatus('loading');

    surveyService
      .getRequest(clinicPath, requestId)
      .then(res => {
        if (cancelled) return;
        setDetail(res);
        setStatus('ready');
      })
      .catch(err => {
        if (cancelled) return;
        const code = err?.response?.status || err?.statusCode;

        if (code === 404) {
          setStatus('notfound');
        } else {
          Sentry.captureException(err);
          setStatus('error');
        }
      });

    return () => {
      cancelled = true;
    };
  }, [clinicPath, requestId]);

  const exitToHome = () => navigate(basePath || '/');

  const alreadyComplete = !!(kind === 'basicInformation'
    ? detail?.status?.basicInformation
    : detail?.status?.insurance);

  return (
    <div className="flex flex-col">
      <div className="mb-4">
        <Link
          to={basePath || '/'}
          className="inline-flex items-center gap-1 text-sm font-medium text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100"
        >
          <ChevronLeftIcon className="size-4" />
          {v.back}
        </Link>
      </div>

      {status === 'loading' && (
        <div className="flex flex-col gap-4">
          <Skeleton className="h-8 w-2/3" />
          <Skeleton className="h-4 w-1/2" />
          <Skeleton className="h-96 w-full rounded-2xl" />
        </div>
      )}

      {status === 'notfound' && (
        <div className="portal-card p-6 text-center">
          <p className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-1">
            {v.notFoundTitle}
          </p>
          <p className="text-sm text-gray-500 dark:text-gray-400">{v.notFoundDetail}</p>
        </div>
      )}

      {detail &&
        (status === 'ready' || status === 'error') &&
        (kind === 'basicInformation' ? (
          <StepBasicInformation
            requestId={detail.id}
            validationCode=""
            language={language}
            primaryColor={primaryColor}
            alreadyComplete={alreadyComplete}
            onComplete={exitToHome}
          />
        ) : (
          <StepInsurance
            requestId={detail.id}
            validationCode=""
            language={language}
            primaryColor={primaryColor}
            alreadyComplete={alreadyComplete}
            onComplete={exitToHome}
          />
        ))}
    </div>
  );
};

export default RequestFormRunner;
